// apps/web/src/pages/vendas/form/anexar-pv-card.tsx — Fase 12, Plano 27 (PV-04, D-04).
// Card "Este cliente já tem PV em aberto." do formulário em modo ?nota=: anexar os itens da NF a um PV em
// aberto ou criar um novo (1 PV -> RadioGroup; vários -> select com "Criar um novo PV" como padrão).
import type { ReactElement } from 'react';
import type { RascunhoPvDaNota } from '@pieltec/contracts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { padraoAnexar, rotuloOpcaoAnexar } from '../../../lib/vendas/pv-da-nota';
import { CampoSelect } from '../../../components/ui/campo-select';

export const DESTINO_NOVO_PV = 'NOVO';

type PvAberto = RascunhoPvDaNota['pvsAbertos'][number];

/** Destino inicial do card: PV único vem marcado para anexar; com vários, o padrão é criar um novo. */
export function destinoInicialAnexar(pvsAbertos: readonly PvAberto[]): string {
  const padrao = padraoAnexar(pvsAbertos.length);
  return padrao.mostrarCard && padrao.padrao === 'ANEXAR' ? (pvsAbertos[0]?.id ?? DESTINO_NOVO_PV) : DESTINO_NOVO_PV;
}

interface AnexarPvCardProps {
  readonly pvsAbertos: readonly PvAberto[];
  /** `DESTINO_NOVO_PV` ou o id do PV escolhido. */
  readonly valor: string;
  readonly onChange: (destino: string) => void;
  readonly desabilitado?: boolean;
}

export function AnexarPvCard({ pvsAbertos, valor, onChange, desabilitado }: AnexarPvCardProps): ReactElement | null {
  const padrao = padraoAnexar(pvsAbertos.length);
  if (!padrao.mostrarCard) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Este cliente já tem PV em aberto.</CardTitle>
      </CardHeader>
      <CardContent>
        {padrao.modo === 'RADIO' && pvsAbertos[0] ? (
          <RadioGroup value={valor} onValueChange={onChange} disabled={desabilitado} aria-label="Destino dos itens da nota">
            <div className="flex items-center gap-2">
              <RadioGroupItem id="anexar-pv-anexar" value={pvsAbertos[0].id} />
              <Label htmlFor="anexar-pv-anexar">{rotuloOpcaoAnexar(pvsAbertos[0])}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem id="anexar-pv-novo" value={DESTINO_NOVO_PV} />
              <Label htmlFor="anexar-pv-novo">Criar um novo PV</Label>
            </div>
          </RadioGroup>
        ) : (
          <div className="flex max-w-md flex-col gap-1.5">
            <Label htmlFor="anexar-pv-select">Escolha o PV</Label>
            <CampoSelect
              id="anexar-pv-select"
              value={valor}
              disabled={desabilitado}
              onValueChange={onChange}
              options={[{ value: DESTINO_NOVO_PV, label: 'Criar um novo PV' }, ...pvsAbertos.map((pv) => ({ value: pv.id, label: rotuloOpcaoAnexar(pv) }))]}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
